/**
 * Analytics Portal V1 — date range control (Phase 9, plan §7 + §12.1).
 *
 * Presets come from the SERVER (`config.presets` / `config.default_preset`) and the custom
 * range only ever sends two calendar dates — every boundary, label, interval and comparison is
 * resolved server-side in the viewer's timezone. The browser performs no date maths beyond a
 * friendly pre-check of the maximum span.
 */
import React, { useState } from "react";
import { CalendarRange, Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";

export const MAX_SPAN_DAYS = 1830;                       // mirrors the API cap (plan A8-3)

export const DateRangeSelector = ({ presets = [], preset, start, end, rangeLabel,
                                    onPreset, onCustomRange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [from, setFrom] = useState(start || "");
  const [to, setTo] = useState(end || "");
  const [problem, setProblem] = useState(null);

  const selectable = presets.filter((option) => option.key !== "custom");
  const activeLabel = rangeLabel
    || (preset === "custom" ? "Custom range"
      : (presets.find((option) => option.key === preset)?.label || "Last 30 days"));

  const applyCustom = () => {
    if (!from || !to) return setProblem("Choose both a start and an end date.");
    if (from > to) return setProblem("The start date must come before the end date.");
    const span = Math.round((new Date(to) - new Date(from)) / 86400000) + 1;
    if (span > MAX_SPAN_DAYS) return setProblem("That range is too long. Choose a shorter period.");
    setProblem(null);
    setCustomOpen(false);
    onCustomRange(from, to);
    return undefined;
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="secondary" size="sm" disabled={disabled}
                  className="justify-between gap-2" data-testid="analytics-range-trigger">
            <CalendarRange className="h-4 w-4" aria-hidden="true" />
            <span className="truncate">{activeLabel}</span>
            <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-1.5">
          <div role="listbox" aria-label="Date range presets">
            {selectable.map((option) => (
              <button
                key={option.key} type="button" role="option"
                aria-selected={option.key === preset}
                onClick={() => { onPreset(option.key); setOpen(false); }}
                className="flex w-full items-center justify-between rounded-md px-2.5 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent"
                data-testid={`analytics-preset-${option.key}`}
              >
                {option.label}
                {option.key === preset && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
              </button>
            ))}
            <button
              type="button" onClick={() => { setOpen(false); setCustomOpen(true); }}
              className="mt-1 flex w-full items-center justify-between rounded-md border-t border-border px-2.5 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent"
              data-testid="analytics-preset-custom"
            >
              Custom range…
              {preset === "custom" && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <Popover open={customOpen} onOpenChange={setCustomOpen}>
        <PopoverTrigger asChild><span className="sr-only" /></PopoverTrigger>
        <PopoverContent align="start" className="w-[280px] space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="analytics-custom-start" className="text-xs">Start date</Label>
            <input id="analytics-custom-start" type="date" value={from}
                   onChange={(event) => setFrom(event.target.value)}
                   className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                   data-testid="analytics-custom-start" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="analytics-custom-end" className="text-xs">End date</Label>
            <input id="analytics-custom-end" type="date" value={to}
                   onChange={(event) => setTo(event.target.value)}
                   className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                   data-testid="analytics-custom-end" />
          </div>
          {problem && (
            <p className="text-xs text-destructive" role="alert"
               data-testid="analytics-custom-error">{problem}</p>
          )}
          <Button size="sm" className="w-full" onClick={applyCustom}
                  data-testid="analytics-custom-apply">Apply range</Button>
        </PopoverContent>
      </Popover>
    </div>
  );
};
